<template>
  <div>
    <v-layout column>
      <v-flex>
        <v-radio-group
          v-model="menuSetting.administrativeDistrictType"
          hide-details
          style="margin-top: 0px;"
        >
          <v-radio
            v-for="typ in categoryTypes"
            :key="typ.value"
            :label="typ.text"
            :value="typ.value"
          />
        </v-radio-group>
      </v-flex>
      <v-flex my-2>
        <div class="red--text">
          対象地域は、都道府県もしくは市区町村のいずれかのみ設定が可能です。
        </div>
      </v-flex>
    </v-layout>
    <v-layout wrap>
      <v-flex xs8>
        <AppTwoColumnsSelector
          v-if="isBroadCategoryType"
          :items="broadCategoryList"
          @changeChosenItems="onChangeSelectedItemsList"
          :available-label="`都道府県一覧`"
          :chosen-label="`配信対象地域`"
          :is-show-all-select="true"
          item-value="id"
          class="large-height-two-columns"
        />
        <AppThreeColumnsSelector
          v-else
          :broad-category-list="broadCategoryList"
          :sub-category-list="subCategoryList"
          :added-id-and-name-list="chosenItemsList"
          :use-search="true"
          @setIds="onChangeSelectedItemsList"
          @setSecondLeftItems="setSecondLeftItems"
          @search="filterItems"
          :broad-category-label="`都道府県一覧`"
          :sub-category-label="`市区町村一覧`"
          :right-label="`配信対象地域`"
          :item-name="`地域`"
          class="large-height-three-columns"
        >
          <slot />
        </AppThreeColumnsSelector>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { MasterRepository } from '@/models/Master/MasterRepository'
import { TargetAreaTypes } from '@/models/CampaignOperation'
import {
  CityIdAndNameModel,
  PrefectureIdAndNameModel,
} from '@/models/CampaignOperation/Form/AreaForm'

const getTypeText = value => {
  switch (value) {
    case TargetAreaTypes.PREFECTURE:
      return '都道府県'
    case TargetAreaTypes.CITY:
      return '市区町村'
    default:
      return ''
  }
}

export default {
  name: 'AppAreaSelector',
  props: {
    menuSetting: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      prefectureList: [],
      subCategoryList: [],
      chosenItemsList: [],
    }
  },
  computed: {
    broadCategoryList() {
      return this.prefectureList.map(prefecture => {
        const isChosen =
          this.menuSetting.area.prefectureAreaObj.prefectureIds.indexOf(
            prefecture.id,
          ) !== -1
        return this.makeItem(prefecture, isChosen)
      })
    },
    categoryTypes() {
      return Object.keys(TargetAreaTypes).map(type => {
        const value = TargetAreaTypes[type]
        return { value, text: getTypeText(value) }
      })
    },
    isBroadCategoryType() {
      return (
        this.menuSetting.administrativeDistrictType ===
        TargetAreaTypes.PREFECTURE
      )
    },
  },
  watch: {
    'menuSetting.administrativeDistrictType'(value) {
      this.clearChosenItemsList(value)
    },
  },
  async created() {
    await this.fetchPrefectureList()
    this.makeChosenItemsList()
  },
  methods: {
    async fetchPrefectureList() {
      this.prefectureList = await MasterRepository.fetchPrefectureList({
        countryId: 1, // 日本のcountryIdは1
      })
    },
    makeItem(item, isChosen) {
      return {
        id: item.id,
        name: item.name,
        selected: false,
        chosen: isChosen,
      }
    },
    makeSubItem(item, parentId, parentName) {
      return {
        parentId: parentId,
        id: item.id,
        parentName: parentName,
        name: item.name,
        selected: false,
      }
    },
    makeChosenItemsList() {
      this.menuSetting.area.cityAreaObj.prefectures.forEach(prefecture => {
        prefecture.cityIdAndNames.forEach(city => {
          this.chosenItemsList.push({
            id: city.id,
            name: city.name,
            parentId: prefecture.prefectureId,
            parentName: prefecture.prefectureName,
            selected: false,
          })
        })
      })
    },
    async setSecondLeftItems(parentId, parentName) {
      const cityList = await this.fetchCityList(parentId)
      this.subCategoryList = cityList.map(item =>
        this.makeSubItem(item, parentId, parentName),
      )
    },
    async fetchCityList(regionId) {
      return await MasterRepository.fetchCityList({
        regionId: regionId,
      })
    },
    onChangeSelectedItemsList(items) {
      if (this.isBroadCategoryType) {
        this.menuSetting.area.prefectureAreaObj.prefectureIds = items
        this.makePrefecturesForm(items)
      } else {
        this.chosenItemsList = items
        this.makeCitiesForm()
      }
    },
    filterItems(keyword) {
      this.subCategoryList = this.subCategoryList.filter(
        item => item.name.indexOf(keyword) !== -1,
      )
    },
    makePrefecturesForm(items) {
      this.menuSetting.area.prefectureAreaObj.prefectureIdAndNames = items.map(
        item => {
          const prefecture = this.prefectureList.find(
            prefecture => prefecture.id === item,
          )
          return PrefectureIdAndNameModel.assign({
            id: prefecture.id,
            name: prefecture.name,
          })
        },
      )
    },
    makeCitiesForm() {
      const prefectureIds = Array.from(
        new Set(this.chosenItemsList.map(item => item.parentId)),
      )
      this.menuSetting.area.cityAreaObj.prefectures = prefectureIds.map(
        prefectureId => {
          const prefecture = this.prefectureList.find(
            prefecture => prefecture.id === prefectureId,
          )
          const cityIdAndNames = this.chosenItemsList
            .filter(item => item.parentId === prefectureId)
            .map(city => {
              return CityIdAndNameModel.assign({
                id: city.id,
                name: city.name,
              })
            })
          const cityIds = cityIdAndNames.map(city => city.id)

          return {
            prefectureId: prefectureId,
            cityIds: cityIds,
            prefectureName: prefecture.name,
            cityIdAndNames: cityIdAndNames,
          }
        },
      )
    },
    clearChosenItemsList(administrativeDistrictType) {
      this.menuSetting.area.clear(administrativeDistrictType)
      if (administrativeDistrictType === TargetAreaTypes.PREFECTURE) {
        this.chosenItemsList = []
      }
    },
  },
}
</script>

<style scoped lang="scss">
.large-height-two-columns {
  ::v-deep .select-list-height {
    height: 230px;
  }
}
.large-height-three-columns {
  ::v-deep .select-list-height {
    height: 230px;
  }
  ::v-deep .small-select-list-height {
    height: 198px;
  }
}
</style>
